import React, { useState, useCallback, useRef } from "react";
import cn from "classnames";

import { LocalisedText } from "../../i18n";
import { PaymentBankTransfer } from "../../types";
import ReceiptAssetItem from "../ReceiptAssetItem";
import DragAndDrop from "../DragAndDrop";
import { MAX_RECEIPT_FILE } from "../../constants";
import { displayPrice } from "../../utils/price";
import Button from "../Button";
import TextWithLink from "../TextWithLink";
import styles from "./style.module.scss";

interface BankTransferFormProps {
  paymentMethodState: PaymentBankTransfer;
  isDisabled: boolean;
  isLoading: boolean;
  totalPrice: number;
  currency: string;
  uploadReceiptFile: (files: File[]) => void;
  handleRemoveAsset: (id: string) => void;
  onSubmit: () => Promise<void>;
}

const BankTransferForm: React.FC<BankTransferFormProps> = (
  props: BankTransferFormProps
) => {
  const {
    paymentMethodState,
    isDisabled,
    isLoading,
    totalPrice,
    currency,
    uploadReceiptFile,
    handleRemoveAsset,
    onSubmit,
  } = props;

  const attachmentFileInput = useRef<HTMLInputElement>(null);
  const onAddAttachmentClicked = useCallback(
    () => attachmentFileInput.current?.click(),
    [attachmentFileInput]
  );
  const onAttachmentPicked = useCallback(
    async (e: React.FormEvent<HTMLInputElement>) => {
      if (isLoading) return;
      const filelist = (e.target as HTMLInputElement).files;
      if (filelist && filelist.length > 0) {
        const files: File[] = [];
        for (let i = 0; i < filelist.length; i++) {
          const f = filelist.item(i);
          if (f) files.push(f);
        }
        if (files.length > 0) {
          uploadReceiptFile(files);
        }
        // clear file input after consumed
        const input = attachmentFileInput.current;
        if (input) {
          input.value = "";
        }
      }
    },
    [isLoading, uploadReceiptFile]
  );

  const [isOnDragOver, setIsOnDragOver] = useState(false);
  const onAttachmentDragOver = useCallback(
    (isOver: boolean) => {
      if (isLoading) return;
      setIsOnDragOver(isOver);
    },
    [isLoading]
  );
  const onAttachmentDrop = useCallback(
    async (files: File[]) => {
      if (isLoading) return;
      setIsOnDragOver(false);
      uploadReceiptFile(files);
    },
    [isLoading, uploadReceiptFile]
  );
  const onAttachmentDragEnter = useCallback(() => {
    if (isLoading) return;
    if (!isOnDragOver) {
      setIsOnDragOver(true);
    }
  }, [isOnDragOver, isLoading]);

  return (
    <div className={styles.bankTransferInfo}>
      <div className={styles.title}>
        <LocalisedText id="view.paymentMethodForm.paymentInformation" />
      </div>
      <div className={styles.description}>
        <LocalisedText id="view.paymentMethodForm.paymentInformationDescription" />
      </div>
      <pre className={styles.section}>
        <TextWithLink textContent={paymentMethodState.noteContent} />
      </pre>
      <div className={cn(styles.section, styles.inColumn)}>
        <div>
          <LocalisedText id="view.paymentMethodForm.outstandingPaymentAmount" />
        </div>
        <div className={styles.paymentAmount}>
          {currency}${displayPrice(totalPrice)}
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.uploadRecipt}>
          <LocalisedText id="view.paymentMethodForm.uploadReceipt" />
        </div>
        {paymentMethodState.receiptAttachments.map(r => (
          <ReceiptAssetItem
            asset={r}
            handleRemoveAsset={handleRemoveAsset}
            isDisabled={isDisabled}
            key={r.id}
          />
        ))}
        {paymentMethodState.receiptAttachments.length < MAX_RECEIPT_FILE && (
          <>
            <input
              type="file"
              ref={attachmentFileInput}
              style={{ display: "none" }}
              multiple={true}
              onChange={onAttachmentPicked}
              accept="image/*"
            />
            <DragAndDrop
              onDragOver={onAttachmentDragOver}
              onDrop={onAttachmentDrop}
              onDragEnter={onAttachmentDragEnter}
            >
              <div
                className={cn(styles.dragAndDropZone, {
                  [styles.overTheme]: isOnDragOver,
                })}
                onClick={onAddAttachmentClicked}
              >
                <div className={styles.dragAndDropText}>
                  <LocalisedText id="view.paymentMethodForm.dragAndDropText" />
                </div>
              </div>
            </DragAndDrop>
          </>
        )}
      </div>
      <div className={styles.buttons}>
        <Button
          isDisabled={isDisabled}
          isLoading={isLoading}
          onClick={onSubmit}
        >
          <LocalisedText id="screen.paymentInfoScreen.submit" />
        </Button>
      </div>
    </div>
  );
};

export default BankTransferForm;
